import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { Badge } from '@shared/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/tabs'
import { Progress } from '@shared/components/ui/progress'
import { Loader2, Activity, AlertTriangle, CheckCircle, TrendingUp, Users, DollarSign, Server, RefreshCw } from 'lucide-react'
import { toast } from '@shared/hooks/use-toast'
import { apiUrl } from '@/lib/api'

interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical'
  uptime: number
  response_time: number
  error_rate: number
  last_updated: string
}

interface PerformanceMetrics {
  api_response_time: number | null
  database_query_time: number
  stripe_api_time: number | null
  webhook_processing_time: number | null
  memory_usage: number
  cpu_usage: number | null
}

interface Alert {
  id: string
  type: 'billing' | 'performance' | 'security' | 'system'
  severity: 'critical' | 'warning' | 'info'
  title: string
  description: string
  created_at: string
  resolved: boolean
}

interface RevenueMetrics {
  total_revenue: number
  monthly_recurring_revenue: number
  revenue_growth: number | null
  churn_rate: number
  average_revenue_per_user: number
}

interface SystemInfo {
  environment: string
  version: string
  node_version: string
  platform: string
  uptime: number
  memory_usage: {
    rss: number
    heapTotal: number
    heapUsed: number
    external: number
  }
  last_restart: string
}

const AdvancedMonitoringPage: React.FC = () => {
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null)
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null)
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [revenueMetrics, setRevenueMetrics] = useState<RevenueMetrics | null>(null)
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    loadMonitoringData()
    // Auto-refresh disabled - manual refresh only
  }, [])

  const loadMonitoringData = async () => {
    try {
      setRefreshing(true)
      
      // Load fast data first (system metrics) - these will display immediately
      const fastDataPromises = [
        fetch(apiUrl('/api/admin/monitoring/health'), {
          headers: await (async()=>{ const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })()
        }).then(async (res) => {
          if (res.ok) {
            const data = await res.json()
            setSystemHealth(data)
          }
        }),
        fetch(apiUrl('/api/admin/monitoring/performance'), {
          headers: await (async()=>{ const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })()
        }).then(async (res) => {
          if (res.ok) {
            const data = await res.json()
            setPerformanceMetrics(data)
          }
        }),
        fetch(apiUrl('/api/admin/monitoring/alerts'), {
          headers: await (async()=>{ const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })()
        }).then(async (res) => {
          if (res.ok) {
            const data = await res.json()
            setAlerts(data.alerts || [])
          }
        }),
        fetch(apiUrl('/api/admin/monitoring/system-info'), {
          headers: await (async()=>{ const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })()
        }).then(async (res) => {
          if (res.ok) {
            const data = await res.json()
            setSystemInfo(data.system_info)
          }
        })
      ]

      // Load revenue data separately (may be slower due to Stripe API)
      const revenuePromise = fetch(apiUrl('/api/admin/monitoring/revenue/analytics'), {
        headers: await (async()=>{ const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })()
      }).then(async (res) => {
        if (res.ok) {
          const data = await res.json()
          setRevenueMetrics(data)
        }
      }).catch((error) => {
        console.error('Revenue data loading error:', error)
        // Don't show error toast for revenue data - it's not critical
      })

      // Wait for fast data to complete first
      await Promise.all(fastDataPromises)
      
      // Then load revenue data (non-blocking)
      revenuePromise

    } catch (error) {
      console.error('Error loading monitoring data:', error)
      toast({ title: 'Error', description: 'Failed to load monitoring data', variant: 'destructive' })
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600'
      case 'warning': return 'text-yellow-600'
      case 'critical': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  const getHealthStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-5 w-5 text-green-600" />
      case 'warning': return <AlertTriangle className="h-5 w-5 text-yellow-600" />
      case 'critical': return <AlertTriangle className="h-5 w-5 text-red-600" />
      default: return <Activity className="h-5 w-5 text-gray-600" />
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200'
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${days}d ${hours}h ${minutes}m`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Advanced Monitoring</h1>
        <Button onClick={loadMonitoringData} disabled={refreshing}>
          {refreshing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* System Health Overview */}
      {systemHealth && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">System Status</CardTitle>
              {getHealthStatusIcon(systemHealth.status)}
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${getHealthStatusColor(systemHealth.status)}`}>
                {systemHealth.status.toUpperCase()}
              </div>
              <p className="text-xs text-muted-foreground">
                Last updated: {new Date(systemHealth.last_updated).toLocaleTimeString()}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Uptime</CardTitle>
              <Server className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatUptime(systemHealth.uptime)}</div>
              <p className="text-xs text-muted-foreground">
                Response time: {systemHealth.response_time}ms
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{systemHealth.error_rate.toFixed(2)}%</div>
              <Progress value={systemHealth.error_rate} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Alerts</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {alerts.filter(alert => !alert.resolved).length}
              </div>
              <p className="text-xs text-muted-foreground">
                {alerts.filter(alert => alert.severity === 'critical' && !alert.resolved).length} critical
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="performance" className="space-y-4">
        <TabsList>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="space-y-4">
          {performanceMetrics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">API Response Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {performanceMetrics.api_response_time !== null ? `${performanceMetrics.api_response_time}ms` : 'N/A'}
                  </div>
                  <Progress value={performanceMetrics.api_response_time ? Math.min(performanceMetrics.api_response_time / 1000 * 100, 100) : 0} className="mt-2" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Database Query Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{performanceMetrics.database_query_time}ms</div>
                  <Progress value={Math.min(performanceMetrics.database_query_time / 100 * 100, 100)} className="mt-2" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Stripe API Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {performanceMetrics.stripe_api_time !== null ? `${performanceMetrics.stripe_api_time}ms` : 'N/A'}
                  </div>
                  <Progress value={performanceMetrics.stripe_api_time ? Math.min(performanceMetrics.stripe_api_time / 2000 * 100, 100) : 0} className="mt-2" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Memory Usage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{performanceMetrics.memory_usage.toFixed(1)}%</div>
                  <Progress value={performanceMetrics.memory_usage} className="mt-2" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">CPU Usage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {performanceMetrics.cpu_usage !== null ? `${performanceMetrics.cpu_usage.toFixed(1)}%` : 'N/A'}
                  </div>
                  <Progress value={performanceMetrics.cpu_usage || 0} className="mt-2" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Webhook Processing</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {performanceMetrics.webhook_processing_time !== null ? `${performanceMetrics.webhook_processing_time}ms` : 'N/A'}
                  </div>
                  <Progress value={performanceMetrics.webhook_processing_time ? Math.min(performanceMetrics.webhook_processing_time / 5000 * 100, 100) : 0} className="mt-2" />
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="revenue" className="space-y-4">
          {revenueMetrics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Total Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(revenueMetrics.total_revenue)}</div>
                  <p className="text-xs text-muted-foreground">All time</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Monthly Recurring Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(revenueMetrics.monthly_recurring_revenue)}</div>
                  <p className="text-xs text-muted-foreground">Current MRR</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Revenue Growth</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {revenueMetrics.revenue_growth !== null ? `+${revenueMetrics.revenue_growth.toFixed(1)}%` : 'N/A'}
                  </div>
                  <p className="text-xs text-muted-foreground">Month over month</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Churn Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{revenueMetrics.churn_rate.toFixed(2)}%</div>
                  <p className="text-xs text-muted-foreground">Monthly churn</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">ARPU</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(revenueMetrics.average_revenue_per_user)}</div>
                  <p className="text-xs text-muted-foreground">Average revenue per user</p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>System Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No active alerts</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {alerts.map((alert, index) => (
                    <div key={alert.id || `alert-${index}`} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        {alert.severity === 'critical' ? (
                          <AlertTriangle className="h-5 w-5 text-red-500" />
                        ) : alert.severity === 'warning' ? (
                          <AlertTriangle className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <CheckCircle className="h-5 w-5 text-blue-500" />
                        )}
                        <div>
                          <h4 className="font-medium">{alert.title}</h4>
                          <p className="text-sm text-gray-600">{alert.description}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(alert.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className={getSeverityColor(alert.severity)}>
                          {alert.severity}
                        </Badge>
                        {alert.resolved && (
                          <Badge variant="secondary">Resolved</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="system" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>System Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Environment:</span>
                  <span className="text-sm font-medium">{systemInfo?.environment || 'development'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Version:</span>
                  <span className="text-sm font-medium">{systemInfo?.version || '1.0.0'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Node Version:</span>
                  <span className="text-sm font-medium">{systemInfo?.node_version || 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Platform:</span>
                  <span className="text-sm font-medium">{systemInfo?.platform || 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Uptime:</span>
                  <span className="text-sm font-medium">
                    {systemInfo ? `${Math.floor(systemInfo.uptime / 3600)}h ${Math.floor((systemInfo.uptime % 3600) / 60)}m` : 'Unknown'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Last Restart:</span>
                  <span className="text-sm font-medium">
                    {systemInfo ? new Date(systemInfo.last_restart).toLocaleString() : 'Unknown'}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Database Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Status:</span>
                  <Badge className="bg-green-100 text-green-800">Connected</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Response Time:</span>
                  <span className="text-sm font-medium">
                    {performanceMetrics?.database_query_time || 0}ms
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Active Connections:</span>
                  <span className="text-sm font-medium">12</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default AdvancedMonitoringPage

